import { produce } from "immer";
import { InfoIcon } from "lucide-react";

import { getText } from "shared/lib";
import { TreePropertyType, type TreeDescriptor } from "./lib";

import { Button, Separator } from "shared/components";
import { FieldSet } from "./treeDialog/FieldSet";
import { DBHField } from "./treeDialog/DBHField";
import { CircumferenceField } from "./treeDialog/CircumferenceField";
import { LocationField } from "./treeDialog/LocationField";
import { LoppingPruningField } from "./treeDialog/LoppingPruningField";
import { ReasonField } from "./treeDialog/ReasonField";
import { TreeDialog } from "./treeDialog/TreeDialog";
import { TreeMeasurementsDialogContainer } from "./treeMeasurementsHelp";

export interface PruningTreeDialogProps {
  onChangeTreeDescriptors?: (treeDescriptors: Array<TreeDescriptor>) => void;
  onClickDelete?: (treeDescriptor: TreeDescriptor) => void;
  onClickNext?: (treeDescriptor: TreeDescriptor) => void;
  onClickPrev?: (treeDescriptor: TreeDescriptor) => void;
  onClickSubmit?: (treeDescriptor: TreeDescriptor) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  treeDescriptor: TreeDescriptor | undefined | null;
  treeDescriptors: Array<TreeDescriptor>;
}

export function PruningTreeDialog({
  onChangeTreeDescriptors,
  onClickDelete,
  onClickNext,
  onClickPrev,
  onClickSubmit,
  onOpenChange,
  open,
  treeDescriptor,
  treeDescriptors,
}: PruningTreeDialogProps) {
  const dbhPropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.dbh,
  );

  const circumferencePropertyExists = treeDescriptor?.properties.has(
    TreePropertyType.circumference,
  );

  const trunkType = treeDescriptor?.properties.get(TreePropertyType.trunk_type);

  const handleChangeTreeDescriptor = (
    changedTreeDescriptor: TreeDescriptor,
  ) => {
    onChangeTreeDescriptors?.(
      produce(treeDescriptors, (draft) => {
        const index = draft.findIndex(
          (td) => td.treeId === changedTreeDescriptor?.treeId,
        );
        if (index > -1) {
          draft[index] = { ...changedTreeDescriptor };
        }
      }),
    );
  };

  const handleClickDelete = () => {
    if (treeDescriptor) {
      onClickDelete?.(treeDescriptor);
    }
    onOpenChange?.(false);
  };

  if (!treeDescriptor) return;

  return (
    <TreeDialog
      onChangeTreeDescriptor={handleChangeTreeDescriptor}
      onClickDelete={handleClickDelete}
      onClickNext={onClickNext}
      onClickPrev={onClickPrev}
      onClickSubmit={onClickSubmit}
      onOpenChange={onOpenChange}
      open={open}
      subTitle={getText("You're proposing to prune this tree.")}
      treeDescriptor={treeDescriptor}
      treeDescriptors={treeDescriptors}
    >
      <FieldSet
        title={
          <span className="flex items-center gap-2">
            {getText("Measurements")}{" "}
            <TreeMeasurementsDialogContainer treeDescriptor={treeDescriptor}>
              {(setOpen) => (
                <Button
                  className="text-bodysm gap-1"
                  onClick={() => setOpen(true)}
                  size="tight"
                  variant="link"
                >
                  <InfoIcon />
                  {getText("More info")}
                </Button>
              )}
            </TreeMeasurementsDialogContainer>
          </span>
        }
        subTitle={getText(
          "Providing accurate measurements helps us better assess your permit requirements.",
        )}
      >
        {dbhPropertyExists && (
          <DBHField
            isMultiStem={trunkType?.value === "multi"}
            onChange={handleChangeTreeDescriptor}
            treeDescriptor={treeDescriptor}
          />
        )}

        {circumferencePropertyExists && (
          <CircumferenceField
            isMultiStem={trunkType?.value === "multi"}
            onChange={handleChangeTreeDescriptor}
            treeDescriptor={treeDescriptor}
          />
        )}

        <LoppingPruningField
          onChange={handleChangeTreeDescriptor}
          treeDescriptor={treeDescriptor}
        />
      </FieldSet>

      <Separator orientation="horizontal" />

      <FieldSet title={getText("Location")}>
        <LocationField
          onChange={handleChangeTreeDescriptor}
          treeDescriptor={treeDescriptor}
        />
      </FieldSet>

      <Separator orientation="horizontal" />

      <ReasonField
        label={getText("Why are you pruning this tree?")}
        onChange={handleChangeTreeDescriptor}
        placeholder={getText("E.g. landscaping, dangerous, in the way")}
        treeDescriptor={treeDescriptor}
      />
    </TreeDialog>
  );
}
